import React from "react";
import styled, { keyframes } from "styled-components";
import { Render, RenderStatus } from "../services/renders-api";

interface RendersCarouselProps {
  renders: Render[];
  loading?: boolean;
  selectedRenderId?: string;
  onRenderSelect?: (render: Render) => void;
}

/**
 * Renders Carousel Component
 * 
 * Displays rendered images in a horizontal scrollable carousel.
 * Shows status indicators for pending/processing/failed renders.
 */
const RendersCarousel: React.FC<RendersCarouselProps> = ({
  renders,
  loading = false,
  selectedRenderId,
  onRenderSelect,
}) => {
  const handleClick = (render: Render) => {
    // Only allow clicking completed renders
    if (render.status === 'completed' && render.imageUrl && onRenderSelect) {
      onRenderSelect(render);
    }
  };

  if (loading) {
    return (
      <CarouselContainer>
        <LoadingMessage>Chargement des rendus...</LoadingMessage>
      </CarouselContainer>
    );
  }

  if (renders.length === 0) {
    return (
      <CarouselContainer>
        <EmptyMessage>Aucun rendu disponible</EmptyMessage>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer>
      <CarouselTrack>
        {renders.map((render) => (
          <Card
            key={render.id}
            onClick={() => handleClick(render)}
            $isClickable={render.status === 'completed' && !!render.imageUrl}
            $isSelected={render.id === selectedRenderId}
          >
            {render.status === 'completed' && render.thumbnailUrl ? (
              <CardImage src={render.thumbnailUrl} alt={render.name} />
            ) : render.status === 'completed' && render.imageUrl ? (
              <CardImage src={render.imageUrl} alt={render.name} />
            ) : (
              <PlaceholderContainer>
                <StatusIndicator status={render.status} />
              </PlaceholderContainer>
            )}
            <StatusBadge status={render.status}>
              {getStatusIcon(render.status)}
            </StatusBadge>
            <CardOverlay>
              <CardName>{render.name}</CardName>
              <CardType>{render.type === '360' ? '360°' : 'Image'}</CardType>
            </CardOverlay>
          </Card>
        ))}
      </CarouselTrack>
    </CarouselContainer>
  );
};

/**
 * Get status icon based on render status
 */
function getStatusIcon(status: RenderStatus): string {
  switch (status) {
    case 'completed':
      return '✓';
    case 'failed':
      return '✕';
    case 'processing':
      return '⟳';
    case 'pending':
      return '⏳';
    default:
      return '?';
  }
}

/**
 * Status indicator component for non-completed renders
 */
const StatusIndicator: React.FC<{ status: RenderStatus }> = ({ status }) => {
  if (status === 'processing' || status === 'pending') {
    return <Spinner />;
  }
  if (status === 'failed') {
    return <FailedIcon>✕</FailedIcon>;
  }
  return null;
};

// Animations
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const CarouselContainer = styled.div`
  display: flex;
  /* flex-direction: column; */
  align-items: center;
  /* justify-content: center; */
  gap: 1rem;
  padding: 1rem;
  flex: 1;
  min-width: 0;
`;

const CarouselTrack = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 1rem;
  width: 100%;
  max-width: 80vw;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  padding: 0.5rem 0 0.5rem 1rem;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Card = styled.div<{ $isClickable: boolean; $isSelected: boolean }>`
  position: relative;
  min-width: 120px;
  width: 120px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: #f5f5f5;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  scroll-snap-align: start;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  border: ${({ $isSelected }) => ($isSelected ? '3px solid #006ab2' : '3px solid transparent')};
  
  &:hover {
    ${({ $isClickable }) =>
      $isClickable &&
      `
      transform: scale(1.05);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    `}
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #ddd;
  border-top-color: #006ab2;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const FailedIcon = styled.div`
  font-size: 24px;
  color: #dc3545;
`;

const StatusBadge = styled.div<{ status: RenderStatus }>`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white;
  background-color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'failed':
        return '#dc3545';
      case 'processing':
        return '#ffc107';
      case 'pending':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  }};
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  padding: 0.5rem 0.4rem 0.3rem;
`;

const CardName = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardType = styled.div`
  font-size: 0.55rem;
  color: rgba(255, 255, 255, 0.8);
`;

const LoadingMessage = styled.div`
  color: #666;
  font-size: 1rem;
  padding: 1rem;
`;

const EmptyMessage = styled.div`
  color: #999;
  font-size: 1rem;
  padding: 1rem;
`;

export default RendersCarousel;
